import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { usePropertyField, useContentToken, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// Shown only when neither a call-tracking number nor a property phone exists.
const PHONE_PLACEHOLDER = '(555) 555-5555';

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export function ResidentAccess() {
  const portalUrl = usePropertyField('property_resident_portal_url') as string || '';
  // Phone is a content token that defaults to the call tracking phone (see contentSchema below).
  // Falls back to property_phone if the property has no tracking number configured.
  const phoneToken = useContentToken('residentaccess.phone') as string | null;
  const fallbackPhone = usePropertyField('property_phone') as string || '';
  // Resolution chain: call-tracking number (token defaultField/override) →
  // property_phone → placeholder. The token's defaultValue is empty so a missing
  // call-tracking number falls through to property_phone instead of the placeholder.
  const phone = phoneToken || fallbackPhone || PHONE_PLACEHOLDER;
  const portalCta = useContentToken('residentaccess.portal_cta');

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="ra-section">
      <div ref={sectionRef as React.RefObject<HTMLDivElement>} className="reveal-fade-up ra-container">
        <RichTextContent
          path="residentaccess.title"
          as="h2"
          className="font-heading text-2xl sm:text-3xl font-normal text-[var(--vibe-color-text)] mb-8 text-center"
        />
        <div className="ra-grid">
          {/* Resident Portal Card */}
          <div className="ra-card">
            <div className="ra-card-icon-wrap ra-card-icon--portal">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2a7.2 7.2 0 01-6-3.22c.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08a7.2 7.2 0 01-6 3.22z" fill="currentColor"/>
              </svg>
            </div>
            <RichTextContent
              path="residentaccess.portal_title"
              as="h3"
              className="ra-card-title"
            />
            <RichTextContent
              path="residentaccess.portal_desc"
              as="p"
              className="ra-card-desc"
            />
            {portalUrl ? (
              <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="ra-btn ra-btn--primary">
                <EditableToken
                  path="residentaccess.portal_cta"
                >
                  {portalCta || 'Access Portal Login'}
                </EditableToken>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </a>
            ) : (
              <span className="ra-btn ra-btn--disabled">Portal URL Not Available</span>
            )}
          </div>

          {/* Need Help Card */}
          <div className="ra-card">
            <div className="ra-card-icon-wrap ra-card-icon--help">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.21 2.2z" fill="currentColor"/>
              </svg>
            </div>
            <RichTextContent
              path="residentaccess.help_title"
              as="h3"
              className="ra-card-title"
            />
            <RichTextContent
              path="residentaccess.help_desc"
              as="p"
              className="ra-card-desc"
            />
            {phone ? (
              // Render a real anchor so the phone is an actual tel: link in the browser.
              // EditableToken lives inside the anchor (as a span) so the displayed number
              // stays selectable/editable in edit mode while the wrapping <a> supplies
              // the href. The EditableToken click handler calls preventDefault, so
              // clicking the number in edit mode won't trigger a phone call.
              <a
                href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                type="call"
                className="dniphone dniphonehref ra-btn ra-btn--outline"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4.64 1.33A.67.67 0 015.33 1H7a.67.67 0 01.64.47l.53 1.6a.67.67 0 01-.17.69L6.88 4.88a8 8 0 003.24 3.24l1.12-1.12a.67.67 0 01.69-.17l1.6.53A.67.67 0 0114 8v1.67a.67.67 0 01-.67.66A10 10 0 013.33 1.67.67.67 0 014 1h.64z" fill="currentColor"/></svg>
                <EditableToken
                  path="residentaccess.phone"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            ) : (
              <span className="ra-btn ra-btn--disabled">Phone Not Available</span>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .ra-section {
          padding: 2rem 0 3.5rem;
          background: white;
        }
        @media (min-width: 768px) {
          .ra-section { padding: 2.5rem 0 4rem; }
        }
        .ra-container {
          max-width: 56rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .ra-container { padding: 0 1.5rem; } }

        .ra-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 768px) {
          .ra-grid { grid-template-columns: 1fr 1fr; }
        }

        .ra-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 2.5rem 2rem;
          background: #f8f9fa;
          border: 1px solid var(--vibe-color-border, #e5e7eb);
          border-radius: var(--vibe-radius-lg, 12px);
          transition: box-shadow 0.25s, transform 0.25s;
        }
        .ra-card:hover {
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
          transform: translateY(-2px);
        }

        .ra-card-icon-wrap {
          width: 56px; height: 56px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 14px;
          margin-bottom: 1.25rem;
        }
        .ra-card-icon--portal {
          background: color-mix(in srgb, var(--vibe-color-primary) 12%, transparent);
          color: var(--vibe-color-primary);
        }
        .ra-card-icon--help {
          background: color-mix(in srgb, var(--vibe-color-primary) 12%, transparent);
          color: var(--vibe-color-primary);
        }

        .ra-card-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--vibe-color-text);
          margin: 0 0 0.75rem;
        }
        .ra-card-desc {
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
          margin: 0 0 1.5rem;
          max-width: 22rem;
        }

        .ra-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          padding: 0.75rem 1.75rem;
          border-radius: 9999px;
          text-decoration: none;
          cursor: pointer;
          transition: opacity 0.2s, transform 0.1s, box-shadow 0.2s;
          margin-top: auto;
        }
        .ra-btn:hover { opacity: 0.9; transform: translateY(-1px); }
        .ra-btn:active { transform: scale(0.98); }

        .ra-btn--primary {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, white);
          border: none;
        }
        .ra-btn--primary:hover {
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }

        .ra-btn--outline {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, white);
          border: none;
        }
        .ra-btn--outline:hover {
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }

        .ra-btn--disabled {
          background: var(--vibe-color-border, #e5e7eb);
          color: var(--vibe-color-text-muted);
          border: none;
          cursor: default;
          opacity: 0.7;
        }
        .ra-btn--disabled:hover { opacity: 0.7; transform: none; }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ResidentAccess',
  displayName: 'Resident Access',
  tokens: {
    'residentaccess.title': {
      label: 'Section Title',
      type: 'richtext',
      defaultValue: 'Current Residents',
    },
    'residentaccess.portal_title': {
      label: 'Portal Card Title',
      type: 'richtext',
      defaultValue: 'Resident Portal',
    },
    'residentaccess.portal_desc': {
      label: 'Portal Card Description',
      type: 'richtext',
      defaultValue: '<p>With convenient 24/7 access, the resident portal makes it easy to request maintenance service, pay your rent online, and manage your account. Log in to get started.</p>',
    },
    'residentaccess.portal_cta': {
      label: 'Portal Button Text',
      type: 'text',
      defaultValue: 'Access Portal Login',
    },
    'residentaccess.help_title': {
      label: 'Help Card Title',
      type: 'richtext',
      defaultValue: 'Need Help? Call Us',
    },
    'residentaccess.help_desc': {
      label: 'Help Card Description',
      type: 'richtext',
      defaultValue: "<p>Our team is here to help with any questions about your lease, maintenance requests, or community guidelines. Don't hesitate to reach out.</p>",
    },
    'residentaccess.phone': {
      label: 'Phone Number',
      type: 'phone',
      // Default to the call-tracking number so analytics can attribute
      // resident calls separately from leasing calls.
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      // Empty so a missing call-tracking number falls through to property_phone
      // in the component (placeholder is applied there as the final fallback).
      defaultValue: '',
    },
  },
};

(ResidentAccess as any).contentSchema = contentSchema;

export default ResidentAccess;
